import apiClient from '../config/axiosConfig';
import { API_BASE_URL } from 'utils/apiUtils';

const validateAccessoryStatus = (status) => {
  if (!status || typeof status !== 'string') {
    throw new Error('Accessory status must be a valid string');
  }
  const validStatuses = ['available', 'purchased', 'activated', 'disabled'];
  if (!validStatuses.includes(status)) {
    throw new Error(`Invalid accessory status. Must be one of: ${validStatuses.join(', ')}`);
  }
  return status;
};

const validateSerialNumber = (serialNumber) => {
  if (!serialNumber || typeof serialNumber !== 'string' || serialNumber.trim() === '') {
    throw new Error('Serial number is required and must be a valid string');
  }
  return serialNumber.trim();
};

const validateUserId = (userId) => {
  if (!userId || typeof userId !== 'string' || userId.trim() === '') {
    throw new Error('User ID is required and must be a valid string');
  }
  return userId.trim();
};

const validateCardId = (cardId) => {
  if (!cardId || typeof cardId !== 'string' || cardId.trim() === '') {
    throw new Error('Card ID is required and must be a valid string');
  }
  return cardId.trim();
};

const validateActivationData = (activationData) => {
  if (!activationData || typeof activationData !== 'object') {
    throw new Error('Activation data must be a valid object');
  }

  if (!activationData.userId) {
    throw new Error('User ID is required in activation data');
  }

  if (!activationData.contentType) {
    throw new Error('Content type is required in activation data');
  }

  if (!activationData.contentData) {
    throw new Error('Content data is required in activation data');
  }

  return {
    ...activationData,
    userId: validateUserId(activationData.userId),
    contentType: validateStringValue(activationData.contentType, 'Content type'),
    contentData: validateObjectValue(activationData.contentData, 'Content data')
  };
};

const validateUpdateData = (updateData) => {
  if (!updateData || typeof updateData !== 'object') {
    throw new Error('Update data must be a valid object');
  }

  const validatedData = { ...updateData };

  if (updateData.status) {
    validatedData.status = validateAccessoryStatus(updateData.status);
  }

  if (updateData.content) {
    validatedData.content = validateObjectValue(updateData.content, 'Content');
  }

  return validatedData;
};

const validateStringValue = (value, fieldName) => {
  if (value !== null && value !== undefined && typeof value !== 'string') {
    throw new Error(`${fieldName} must be a string`);
  }
  return value;
};

const validateObjectValue = (value, fieldName) => {
  if (!value || typeof value !== 'object') {
    throw new Error(`${fieldName} must be a valid object`);
  }
  return value;
};

const validateEventType = (eventType) => {
  if (!eventType || typeof eventType !== 'string' || eventType.trim() === '') {
    throw new Error('Event type is required and must be a valid string');
  }
  return eventType.trim();
};

const validateEventData = (eventData) => {
  if (eventData && typeof eventData !== 'object') {
    throw new Error('Event data must be a valid object');
  }
  return eventData || {};
};

const formatSpecialLinks = (type, url) => {
  if (!type || !url) {
    return url;
  }

  if (type === 'WhatsApp') {
    // Remove any existing WhatsApp formatting
    const cleanNumber = url.replace(/^https?:\/\/wa\.me\//, '').replace(/^\+/, '');
    return `https://wa.me/${cleanNumber}`;
  }
  return url;
};

const handleApiError = (error, operation = 'API operation') => {
  console.error(`[AccessoryAPI] ${operation} Error:`, error.message);

  // Handle specific error cases
  if (error.response?.status === 401) {
    throw new Error('Authentication required. Please sign in to continue.');
  }

  if (error.response?.status === 403) {
    throw new Error('Access denied. You do not have permission to perform this action.');
  }

  if (error.response?.status === 404) {
    throw new Error('Accessory is not found or no longer available.');
  }

  if (error.response?.status === 409) {
    throw new Error('Accessory is already in use or conflicts with existing data.');
  }

  if (error.response?.status === 422) {
    const validationErrors = error.response?.data?.errors || [];
    const errorMessage = validationErrors.length > 0
      ? `Validation errors: ${validationErrors.join(', ')}`
      : 'Invalid data provided';
    throw new Error(errorMessage);
  }

  if (error.response?.status >= 500) {
    throw new Error('Server error. Please try again later.');
  }

  if (error.code === 'ERR_NETWORK') {
    throw new Error('Network error. Please check your connection and try again.');
  }

  if (error.code === 'ECONNABORTED') {
    throw new Error('Request timeout. Please try again.');
  }

  // Default error message
  const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
  throw new Error(errorMessage);
};

export const createAccessorySession = async (userId) => {
  try {
    const validUserId = validateUserId(userId);

    const response = await apiClient.post(
      `${API_BASE_URL}/api/accessory/session/create`,
      { userId: validUserId }
    );

    if (response.data.sessionId) {
      sessionStorage.setItem('accessorySession', response.data.sessionId);
    }

    return response.data;
  } catch (error) {
    handleApiError(error, 'Creating accessory session');
  }
};

export const handleAccessoryScan = async (serialNumber, scanType = 'qr') => {
  try {
    const validSerialNumber = validateSerialNumber(serialNumber);
    const validScanType = validateStringValue(scanType, 'Scan type') || 'qr';

    const isLegacyFormat = /^[A-Z0-9]{5,7}$/.test(validSerialNumber);

    const legacyUrlMatch = typeof validSerialNumber === 'string' &&
      validSerialNumber.match(/intouchwe\.com\/view-profile\/([A-Za-z0-9-]+)/i);

    let effectiveSerialNumber = validSerialNumber;
    let isLegacy = false;

    if (legacyUrlMatch && legacyUrlMatch[1]) {
      effectiveSerialNumber = legacyUrlMatch[1];
      isLegacy = true;
    } else if (isLegacyFormat) {
      isLegacy = true;
    }

    const url = `${API_BASE_URL}/api/accessory/scan/${effectiveSerialNumber}?scanType=${validScanType}${isLegacy ? '&isLegacy=true' : ''}`;

    const scanResponse = await apiClient.get(url);

    return {
      success: true,
      ...scanResponse.data
    };
  } catch (error) {
    if (error.response && error.response.status === 404) {
      return {
        success: false,
        exists: false,
        message: 'Accessory not found'
      };
    }

    if (error.code === 'ERR_NETWORK') {
      return {
        success: false,
        exists: false,
        message: 'Network error, please try again'
      };
    }

    // For other errors, handle normally
    try {
      handleApiError(error, 'Handling accessory scan');
    } catch (processedError) {
      return {
        success: false,
        message: processedError.message
      };
    }
  }
};

export const getAccessoryBySerialNumber = async (serialNumber) => {
  try {
    const validSerialNumber = validateSerialNumber(serialNumber);

    try {
      const scanResponse = await handleAccessoryScan(validSerialNumber);

      if (scanResponse && scanResponse.success && scanResponse.accessory) {
        return {
          success: true,
          accessory: scanResponse.accessory
        };
      }
    } catch (scanError) {
      // Continue to try direct endpoint
    }

    const url = `${API_BASE_URL}/api/accessory/${validSerialNumber}`;
    const response = await apiClient.get(url);

    if (!response.data.success) {
      return null;
    }

    if (!response.data.accessory) {
      return null;
    }

    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 401) {
      return {
        success: true,
        accessory: {
          serialNumber,
          requiresAuth: true,
          status: 'pending_auth',
          _id: 'placeholder'
        }
      };
    }

    if (error.response && error.response.status === 404) {
      return null;
    }

    if (error.code === 'ERR_NETWORK') {
      return null;
    }

    try {
      handleApiError(error, 'Fetching accessory by serial number');
    } catch (processedError) {
      return null;
    }
  }
};

export const createAccessory = async (serialNumber) => {
  try {
    const validSerialNumber = validateSerialNumber(serialNumber);

    const response = await apiClient.post(
      `${API_BASE_URL}/api/accessory/create`,
      { serialNumber: validSerialNumber }
    );

    return response.data.accessory;
  } catch (error) {
    handleApiError(error, 'Creating accessory');
  }
};

export const activateAccessory = async (serialNumber, activationData) => {
  try {
    const validSerialNumber = validateSerialNumber(serialNumber);
    const validActivationData = validateActivationData(activationData);

    const response = await apiClient.post(
      `${API_BASE_URL}/api/accessory/activate/${validSerialNumber}`,
      validActivationData
    );

    return {
      success: true,
      accessory: response.data.accessory
    };
  } catch (error) {
    handleApiError(error, 'Activating accessory');
  }
};

export const handleActivation = async (serialNumber, userId, cardId) => {
  try {
    const validSerialNumber = validateSerialNumber(serialNumber);
    const validUserId = validateUserId(userId);
    const validCardId = validateCardId(cardId);

    const activationData = {
      userId: validUserId,
      contentType: 'business_card',
      contentData: {
        cardId: validCardId,
        type: 'card_link'
      }
    };

    const result = await activateAccessory(validSerialNumber, activationData);
    return result;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to activate accessory';
    throw new Error(errorMessage);
  }
};

export const getUserAccessories = async (userId) => {
  try {
    const validUserId = validateUserId(userId);

    const response = await apiClient.get(
      `${API_BASE_URL}/api/accessory/user/${validUserId}`
    );

    return response.data;
  } catch (error) {
    handleApiError(error, 'Fetching user accessories');
  }
};

export const updateAccessory = async (serialNumber, updateData) => {
  try {
    const validSerialNumber = validateSerialNumber(serialNumber);
    const validUpdateData = validateUpdateData(updateData);

    if (validUpdateData.content?.type && validUpdateData.content.data?.linkUrl) {
      validUpdateData.content.data.formattedLink = formatSpecialLinks(
        validUpdateData.content.type,
        validUpdateData.content.data.linkUrl
      );
    }

    const response = await apiClient.put(
      `${API_BASE_URL}/api/accessory/update/${validSerialNumber}`,
      validUpdateData
    );

    return response.data;
  } catch (error) {
    handleApiError(error, 'Updating accessory');
  }
};

export const deactivateAccessory = async (serialNumber) => {
  try {
    const validSerialNumber = validateSerialNumber(serialNumber);

    const response = await apiClient.post(
      `${API_BASE_URL}/api/accessory/${validSerialNumber}/deactivate`
    );

    return response.data;
  } catch (error) {
    handleApiError(error, 'Deactivating accessory');
  }
};

export const deleteAccessory = async (serialNumber) => {
  try {
    const validSerialNumber = validateSerialNumber(serialNumber);

    const response = await apiClient.delete(
      `${API_BASE_URL}/api/accessory/${validSerialNumber}`
    );

    return response.data;
  } catch (error) {
    handleApiError(error, 'Deleting accessory');
  }
};

export const unmapAccessory = async (serialNumber) => {
  try {
    const validSerialNumber = validateSerialNumber(serialNumber);

    const response = await apiClient.post(
      `${API_BASE_URL}/api/accessory/${validSerialNumber}/unmap`
    );

    return response.data;
  } catch (error) {
    handleApiError(error, 'Unmapping accessory');
  }
};

export const trackAccessoryAnalytics = async (serialNumber, eventType, eventData) => {
  try {
    const validSerialNumber = validateSerialNumber(serialNumber);
    const validEventType = validateEventType(eventType);
    const validEventData = validateEventData(eventData);

    const response = await apiClient.post(
      `${API_BASE_URL}/api/accessory/${validSerialNumber}/analytics`,
      {
        eventType: validEventType,
        eventData: validEventData
      }
    );

    return response.data;
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to track analytics',
      timestamp: new Date().toISOString()
    };
  }
};